// The "start a signup" goal, and why it has its own module.
//
// First real traffic (2026-09-21) was 7 visitors whose entry and exit page were both
// `/`. Nobody reached a second page. Bounce rate alone cannot separate "read it and
// declined" from "never engaged" — a single-pageview visit reports 0s duration
// whichever it was. The loaded script (v36) does send an `engagement` event carrying
// scroll depth and engaged time, so attention is measurable; what is missing is
// INTENT, and only a click on the call to action proves that. (#767)
//
// The event name lives here rather than at each call site because a typo does not
// fail — it silently creates a second goal in Plausible that nobody is watching.

import { detectInAppBrowser } from '../utils/inAppBrowser';
import { trackEvent } from './plausible';

/**
 * Must match the goal created in the Plausible dashboard exactly, including case.
 * Pairs with the existing `Signup` event on a completed registration, giving a
 * three-stage funnel: pageview, Signup Started, Signup.
 */
export const CTA_GOAL = 'Signup Started';

/**
 * Which call to action was pressed.
 *
 * Every route into `/register` must be listed, or the funnel lies: `Signup` fires for
 * any completed registration, so leaving an entry point untracked lets stage 3 exceed
 * stage 2 and the conversion rate read above 100% — a wrong number that looks like a
 * real one.
 *
 * `hero` and `footer` are the two ends of the landing page, which is worth separating:
 * a hero click is an immediate decision, a footer click means they read the whole page.
 */
export type CtaLocation =
  | 'hero'
  | 'footer'
  | 'compare'
  | 'features'
  // The assistant's own page (#800). Kept separate from `features` rather than
  // folded into it: the whole reason that page exists is to find out whether the
  // assistant converts on its own, which a shared label would hide.
  //
  // Split hero from footer for the same reason `hero` and `footer` are split on the
  // landing page — and more so here. This page is long (nine screens on a phone), so
  // a footer click means someone read the whole argument, which is precisely what we
  // want to know about a deep-dive page.
  | 'assistant'
  | 'assistant-footer'
  | 'pricing'
  // The November challenge page (#1020), hero and footer apart as on the landing page.
  | 'november'
  | 'november-footer'
  // The fantasy writing software page (#1136), hero and footer apart as on the landing page.
  | 'fantasy'
  | 'fantasy-footer';

/**
 * Record a click on a call to action that starts a signup.
 *
 * No-ops with analytics unconfigured, because `trackEvent` does. Nothing is awaited:
 * React Router navigates client-side without unloading the document, so the beacon is
 * never cut off, and making the visitor wait would put analytics in front of the thing
 * they actually asked for.
 *
 * The try/catch is load-bearing rather than defensive habit. React Router runs the
 * onClick handler BEFORE it navigates, and only navigates if the event was not
 * defaultPrevented — so a throw in here leaves the visitor on a dead button. That
 * turns an analytics fault into a lost conversion. A missing event is an acceptable
 * cost; a call to action that does nothing is not.
 */
export function trackCtaClick(location: CtaLocation): void {
  try {
    // Which app's browser the click came from, so the funnel can be split there (#937).
    const inApp = detectInAppBrowser()?.app;
    trackEvent(CTA_GOAL, inApp ? { location, inApp } : { location });
  } catch {
    // Never let measurement break the thing being measured.
  }
}
